import type { PaigeUploadProgress } from './paige.global';
import { whileHeld } from './page-holds';
import { trackProduct } from './product-tracker';
import { responseWords } from './problem-words';

/** Where a job's files are uploaded: the job's channel (`/v1/jobs/{id}`) and the tenant to act in. */
export interface UploadChannel {
  engineOrigin: string;
  tenant: string | undefined;
}

/**
 * What the engine kept of the files: one token per request, the paths a process request names, and
 * the names Paige could not read. One unreadable file no longer costs the request the rest of it
 * (ADR-0012), so the ones that did not go up have to be said out loud.
 */
export interface UploadReceipt {
  token: string;
  files: string[];
  skipped: string[];
}

/** The kinds of file Paige reads; the engine keeps nothing else (WorkspaceIntake.Supported). */
export const supportedExtensions = [
  '.pdf',
  '.png',
  '.jpg',
  '.jpeg',
  '.tif',
  '.tiff',
  '.bmp',
  '.gif',
];

export const accept = supportedExtensions.join(',');

export function isSupported(name: string): boolean {
  const dot = name.lastIndexOf('.');
  return dot >= 0 && supportedExtensions.includes(name.slice(dot).toLowerCase());
}

/** Files per request: enough that a folder goes up in a few round trips, few enough that n of m moves. */
const batchSize = 20;
/**
 * At most this many bytes in one request: the API takes 30,000,000 (UploadLimits), and a request is framing
 * plus the files. A long colour scan (20 pages at 2 MB) would otherwise be refused 413 whole.
 */
export const batchBytes = 24 * 1024 * 1024;

/** The files in request-sized groups, in order: at most `batchSize` files and `batchBytes` bytes each (a bigger file alone). */
export function batchesOf<T extends { size: number }>(
  files: T[],
  maxFiles = batchSize,
  maxBytes = batchBytes,
): T[][] {
  const groups: T[][] = [];
  let group: T[] = [];
  let bytes = 0;
  for (const file of files) {
    if (group.length > 0 && (group.length >= maxFiles || bytes + file.size > maxBytes)) {
      groups.push(group);
      group = [];
      bytes = 0;
    }
    group.push(file);
    bytes += file.size;
  }
  if (group.length > 0) groups.push(group);
  return groups;
}

/** How long the line keeps naming what did not go up, once the rest has. */
export const skippedLinger = 6000;

/**
 * Takes the upload line down. When something was left behind - a kind Paige does not read, or a
 * file whose bytes did not decode - the line stays up long enough to be read first: one bad file no
 * longer stops the whole import (ADR-0012), so this line is the only place the operator learns that
 * one of their files did not go.
 */
export function clearUploadLine(final: PaigeUploadProgress): void {
  const show = window.paigeWorkbench?.upload;
  if (!show) return;
  if (!final.skipped?.length && !final.unreadable?.length) {
    show(null);
    return;
  }
  show(final);
  setTimeout(() => show(null), skippedLinger);
}

/**
 * Files into the job's workspace through `POST {engineOrigin}/import`, a batch at a time so the
 * progress line can count (n of m). Every request answers with its own token; the receipt joins
 * the paths, under the first token, which is what the staged capture is named after. A refusal
 * (no engine holds the job, the session ended) becomes an Error carrying Paige's reason. The page
 * is held while the files go up, so Paige never reloads itself onto a newer version under them
 * (page-holds.ts). Pages from a scanner say so (`scanned`, `?source=scanner`): the job's engine splits a scan stack
 * where it finds documents and keeps each file a person chose as one document (source-aware separation,
 * docs/runbooks/break-sheets.md).
 */
export function uploadFiles(
  channel: UploadChannel,
  files: File[],
  onProgress?: (progress: PaigeUploadProgress) => void,
  source: UploadSource = 'upload',
  scanned = false,
): Promise<UploadReceipt> {
  return whileHeld('upload', () => sendFiles(channel, files, onProgress, { source, scanned }));
}

async function sendFiles(
  channel: UploadChannel,
  files: File[],
  onProgress: ((progress: PaigeUploadProgress) => void) | undefined,
  { source, scanned }: { source: UploadSource; scanned: boolean },
): Promise<UploadReceipt> {
  const receipt: UploadReceipt = { token: '', files: [], skipped: [] };
  trackProduct('pages.upload_started', { count: files.length, source });
  let sent = 0;
  for (const batch of batchesOf(files)) {
    onProgress?.({ sent, total: files.length, unreadable: [...receipt.skipped] });
    sent += batch.length;
    const part = await uploadBatch(channel, batch, scanned).catch((error: unknown) => {
      trackProduct('pages.upload_failed', { reason: failureKind(error) });
      throw error;
    });
    receipt.token ||= part.token;
    receipt.files.push(...part.files);
    receipt.skipped.push(...part.skipped);
  }
  onProgress?.({ sent: files.length, total: files.length, unreadable: [...receipt.skipped] });
  trackProduct('pages.upload_finished', { count: receipt.files.length });
  return receipt;
}

/** Where the files came from, for the product event: the file picker or a folder. */
export type UploadSource = 'upload' | 'folder';

/** A refused upload carries its status; anything else (offline, aborted) is the network. */
class UploadRefused extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

/** The kind of failure and nothing more: `refused_413`, `network`; never the message. */
export function failureKind(error: unknown): string {
  return error instanceof UploadRefused ? `refused_${error.status}` : 'network';
}

async function uploadBatch(
  channel: UploadChannel,
  files: File[],
  scanned: boolean,
): Promise<UploadReceipt> {
  const body = new FormData();
  for (const file of files) body.append('files', file, file.name);
  const response = await fetch(
    `${channel.engineOrigin}/import${scanned ? '?source=scanner' : ''}`,
    {
      method: 'POST',
      credentials: 'include',
      headers: channel.tenant ? { 'Paige-Tenant': channel.tenant } : {},
      body,
    },
  );
  if (!response.ok) throw new UploadRefused(await refusalOf(response), response.status);
  const part = (await response.json()) as Partial<UploadReceipt>;
  return { token: part.token ?? '', files: part.files ?? [], skipped: part.skipped ?? [] };
}

/** Why the upload was refused, in Paige's words: its own sentence for a person, else a plain line (problem-words.ts). */
async function refusalOf(response: Response): Promise<string> {
  return `The pages could not be added. ${await responseWords(response, 'Nothing was lost; try again in a minute.')}`;
}
